import React, { useState, useEffect, useRef } from 'react';
import { usePlaylists } from './PlaylistContext';
import { Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import JSZip from 'jszip';
// Using new toast styling system from _toasts.scss

const CONCURRENCY_LIMIT = 5; // Number of parallel fetches

const PlaylistsRow = ({ tracks: propTracks }) => {
  const {
    playlists,
    error,
    addPlaylist,
    deletePlaylist,
    focusedPlaylists,
    toggleFocusedPlaylist,
    selectedPlaylist,
    selectPlaylist,
    updatePlaylistName,
    clearPlaylistTracks,
    playlistTrackIds,
    tracks: contextTracks
  } = usePlaylists();
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [playlistToClear, setPlaylistToClear] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadModalInfo, setDownloadModalInfo] = useState(null); // { playlist, fileType, trackCount }
  const [downloadProgress, setDownloadProgress] = useState({ show: false, current: 0, total: 0 });
  const [toast, setToast] = useState({ show: false, message: '' });
  const popoverButtonRefs = useRef({});
  const popoverRefs = useRef({});

  // Helper to show toast
  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await addPlaylist(newName, newDesc);
    showToast(`Playlist "${newName}" added!`);
    setNewName('');
    setNewDesc('');
    setShowAdd(false);
  };

  const handleRename = async (playlistId) => {
    if (!renameValue.trim()) return;
    if (typeof playlists.find(p => p.id === playlistId)?.name === 'string') {
      await updatePlaylistName(playlistId, renameValue);
    }
    setRenamingId(null);
    setRenameValue('');
  };

  // Helper: fetch with concurrency limit
  async function fetchWithConcurrencyLimit(tasks, limit, onProgress) {
    let i = 0;
    const results = new Array(tasks.length);
    const queue = [];
    function runNext() {
      if (i >= tasks.length) return Promise.resolve();
      const idx = i++;
      const p = tasks[idx]().then(result => {
        results[idx] = result;
        if (onProgress) onProgress(i);
        return runNext();
      });
      return p;
    }
    for (let j = 0; j < Math.min(limit, tasks.length); j++) {
      queue.push(runNext());
    }
    await Promise.all(queue);
    return results;
  }

  const handleDownloadAllTracks = async (playlistId, fileType = 'mp3') => {
    const allTracks = propTracks || Object.values(contextTracks || {});
    const trackIds = playlistTrackIds[playlistId] || [];
    const tracksToDownload = allTracks.filter(track => trackIds.includes(track.id));
    setDownloadProgress({ show: true, current: 0, total: tracksToDownload.length });
    const zip = new JSZip();
    // Prepare fetch tasks
    const fetchTasks = tracksToDownload.map((track) => {
      return async () => {
        let fileObj = null;
        if (fileType === 'mp3') {
          fileObj = Array.isArray(track.mp3Files) && track.mp3Files.length > 0 ? track.mp3Files[0] : null;
        } else if (fileType === 'wav') {
          fileObj = Array.isArray(track.wavFiles) && track.wavFiles.length > 0 ? track.wavFiles[0] : null;
        }
        if (fileObj && fileObj.downloadUrl) {
          try {
            const response = await fetch(fileObj.downloadUrl);
            const blob = await response.blob();
            return { fileName: fileObj.fileName || `download.${fileType}`, blob };
          } catch (err) {
            console.warn(`Failed to fetch ${fileType} for track:`, track, err);
            return null;
          }
        } else {
          console.warn(`No downloadable ${fileType} file for track:`, track);
          return null;
        }
      };
    });
    // Fetch in parallel with progress
    const onProgress = (done) => setDownloadProgress((p) => ({ ...p, current: done }));
    const results = await fetchWithConcurrencyLimit(fetchTasks, CONCURRENCY_LIMIT, onProgress);
    // Add to zip
    results.forEach((result) => {
      if (result) {
        zip.file(result.fileName, result.blob);
      }
    });
    const playlist = playlists.find(p => p.id === playlistId);
    const playlistName = playlist ? playlist.name.replace(/[^a-zA-Z0-9 ]/g, '_') : 'playlist';
    const ext = fileType === 'mp3' ? '.mp3' : '.wave';
    const zipFileName = `${playlistName}_Vault Playlist${ext}.zip`;
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    setDownloadProgress({ show: false, current: 0, total: 0 });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipFileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeletePlaylist = async (playlist) => {
    await deletePlaylist(playlist.id);
    showToast(`Playlist "${playlist.name}" deleted!`);
    setShowDeleteModal(false);
    setPlaylistToDelete(null);
  };

  const handleClearPlaylist = async (playlist) => {
    await clearPlaylistTracks(playlist.id);
    showToast(`Playlist "${playlist.name}" cleared!`);
    setShowClearModal(false);
    setPlaylistToClear(null);
  };

  // Click-away handler for popover
  useEffect(() => {
    if (popoverOpen === null) return;
    function handleClickAway(event) {
      // Check if click is inside the popover or the button
      const button = popoverButtonRefs.current[popoverOpen];
      const popover = popoverRefs.current[popoverOpen];
      if (
        (button && button.contains(event.target)) ||
        (popover && popover.contains(event.target))
      ) {
        return;
      }
      setPopoverOpen(null);
    }
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [popoverOpen]);

  return (
    <div className="playlists-row-container" style={{ minHeight: '30px' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-container">
          <div className="alert alert-success" style={{ backgroundColor: 'var(--button-success)', color: '#fff', border: 'none' }}>
            <strong>{toast.message}</strong>
          </div>
        </div>
      )}
      <div className="playlists-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'nowrap', height: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', flex: 1, alignItems: 'center', height: '100%' }}>
          {playlists.map((playlist) => (
            <div
              key={playlist.id}
              className={`playlist-item${selectedPlaylist === playlist.id ? ' selected' : ''}${focusedPlaylists.includes(playlist.id) ? ' focused' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                cursor: 'pointer',
                background:
                  selectedPlaylist === playlist.id
                    ? 'var(--accent-1-color)'
                    : focusedPlaylists.includes(playlist.id)
                      ? 'var(--button-primary-state)'
                      : 'transparent',
                color: selectedPlaylist === playlist.id ? '#fff' : undefined,
                height: '30px'
              }}
            >
              {/* Checkbox cell */}
              <div style={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Checkbox
                  checked={focusedPlaylists.includes(playlist.id)}
                  onChange={() => toggleFocusedPlaylist(playlist.id)}
                  style={{ marginRight: 0, padding: 0, height: '100%', width: '100%' }}
                  size="small"
                  icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                  checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                  inputProps={{ title: "Focus for adding tracks" }}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
                      e.preventDefault();
                      e.target.blur();
                    }
                  }}
                />
              </div>
              {/* Label cell */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <span 
                  onClick={() => selectPlaylist(selectedPlaylist === playlist.id ? null : playlist.id)} 
                  style={{ 
                    fontWeight: 'normal', 
                    color: (selectedPlaylist === playlist.id || focusedPlaylists.includes(playlist.id)) ? '#fff' : 'rgba(255,255,255,0.5)'
                  }}
                >
                  {playlist.name}
                </span>
              </div>
              {/* 3-dots cell */}
              <div className="playlist-dropdown" style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <button
                  id={`playlist-actions-${playlist.id}`}
                  ref={el => (popoverButtonRefs.current[playlist.id] = el)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    color: (selectedPlaylist === playlist.id || focusedPlaylists.includes(playlist.id)) ? '#fff' : 'rgba(255,255,255,0.5)',
                    padding: 0,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'color 0.2s',
                  }}
                  title="More options"
                  onClick={e => {
                    e.stopPropagation();
                    setPopoverOpen(popoverOpen === playlist.id ? null : playlist.id);
                  }}
                  onMouseOver={e => (e.currentTarget.style.color = '#ff4b4b')}
                  onMouseOut={e => (e.currentTarget.style.color = (selectedPlaylist === playlist.id || focusedPlaylists.includes(playlist.id)) ? '#fff' : 'rgba(255,255,255,0.5)')}
                >
                  &#8942;
                </button>
                <Popover
                  placement="bottom"
                  isOpen={popoverOpen === playlist.id}
                  target={`playlist-actions-${playlist.id}`}
                  toggle={() => setPopoverOpen(popoverOpen === playlist.id ? null : playlist.id)}
                  fade={false}
                  modifiers={{ preventOverflow: { enabled: true } }}
                  innerRef={el => (popoverRefs.current[playlist.id] = el)}
                >
                  <PopoverHeader>Playlist Actions</PopoverHeader>
                  <PopoverBody>
                    {renamingId === playlist.id ? (
                      <div style={{ padding: 8 }}>
                        <input
                          type="text"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          className="playlist-input playlist-input-rename"
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename(playlist.id);
                            if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                          }}
                        />
                        <button 
                          style={{ 
                            marginRight: 4, 
                            background: 'var(--accent-1-color)', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 4, 
                            padding: '0.25rem 0.75rem', 
                            fontWeight: 500, 
                            cursor: 'pointer',
                            transition: 'background 0.18s'
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#e05555'}
                          onMouseOut={e => e.currentTarget.style.background = 'var(--accent-1-color)'}
                          onClick={() => handleRename(playlist.id)}
                        >Save</button>
                        <button 
                          style={{ 
                            background: 'var(--button-primary)', 
                            color: '#000', 
                            border: 'none', 
                            borderRadius: 4, 
                            padding: '0.25rem 0.75rem', 
                            fontWeight: 500, 
                            cursor: 'pointer',
                            transition: 'background 0.18s'
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#e0e0e0'}
                          onMouseOut={e => e.currentTarget.style.background = 'var(--button-primary)'}
                          onClick={() => { setRenamingId(null); setRenameValue(''); }}
                        >Cancel</button>
                      </div>
                    ) : (
                      <>
                        <button
                          className="popover-action"
                          onClick={() => { setRenamingId(playlist.id); setRenameValue(playlist.name); }}
                        >
                          <span>Rename</span>
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="popover-action danger"
                          onClick={() => { setPlaylistToDelete(playlist); setShowDeleteModal(true); setPopoverOpen(null); }}
                        >
                          <span>Delete</span>
                          <i className="fas fa-trash"></i>
                        </button>
                        <button
                          className="popover-action warning"
                          onClick={() => { setPlaylistToClear(playlist); setShowClearModal(true); setPopoverOpen(null); }}
                        >
                          <span>Clear playlist</span>
                          <i className="fas fa-minus-circle"></i>
                        </button>
                        <button
                          className="popover-action"
                          style={{
                            cursor: selectedPlaylist === playlist.id ? 'pointer' : 'not-allowed',
                            opacity: selectedPlaylist === playlist.id ? 1 : 0.5,
                            justifyContent: 'space-between'
                          }}
                          onClick={() => {
                            if (selectedPlaylist === playlist.id) {
                              const allTracks = propTracks || Object.values(contextTracks || {});
                              const trackIds = playlistTrackIds[playlist.id] || [];
                              const tracksToDownload = allTracks.filter(track => trackIds.includes(track.id));
                              setDownloadModalInfo({ playlist, fileType: 'mp3', trackCount: tracksToDownload.length });
                              setShowDownloadModal(true);
                              setPopoverOpen(null);
                            }
                          }}
                          disabled={selectedPlaylist !== playlist.id}
                        >
                          <span>Download playlist .mp3</span>
                          <i className="fas fa-download"></i>
                        </button>
                        <button
                          className="popover-action"
                          style={{
                            cursor: selectedPlaylist === playlist.id ? 'pointer' : 'not-allowed',
                            opacity: selectedPlaylist === playlist.id ? 1 : 0.5,
                            justifyContent: 'space-between'
                          }}
                          onClick={() => {
                            if (selectedPlaylist === playlist.id) {
                              const allTracks = propTracks || Object.values(contextTracks || {});
                              const trackIds = playlistTrackIds[playlist.id] || [];
                              const tracksToDownload = allTracks.filter(track => trackIds.includes(track.id));
                              setDownloadModalInfo({ playlist, fileType: 'wav', trackCount: tracksToDownload.length });
                              setShowDownloadModal(true);
                              setPopoverOpen(null);
                            }
                          }}
                          disabled={selectedPlaylist !== playlist.id}
                        >
                          <span>Download playlist .wave</span>
                          <i className="fas fa-download"></i>
                        </button>
                      </>
                    )}
                  </PopoverBody>
                </Popover>
              </div>
            </div>
          ))}
        </div>
        <div style={{ flex: '0 0 100px', display: 'flex', alignItems: 'stretch', height: '100%' }}>
          {showAdd ? (
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: 4 }}>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Playlist name"
                required
                className="playlist-input playlist-input-create"
                autoFocus
              />
              <button type="submit" style={{ background: 'var(--accent-1-color)', color: '#fff', border: 'none', borderRadius: 4, padding: '0.25rem 0.75rem', height: '30px' }}>Add</button>
              <button type="button" onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', color: 'var(--accent-1-color)', cursor: 'pointer', height: '30px' }}>Cancel</button>
            </form>
          ) : (
            <button 
              className="add-playlist-btn btn-primary-custom"
              onClick={() => setShowAdd(true)}
              style={{ border: 'none', borderRadius: 4, height: '30px', width: '100%', fontWeight: '400 !important' }}
            >
              + Add
            </button>
          )}
        </div>
      </div>
      {error && <div className="playlist-error" style={{ color: 'red', marginTop: 8 }}>{error.message || String(error)}</div>}
      {/* Custom Delete Playlist Modal */}
      {showDeleteModal && playlistToDelete && (
        <div className="modal-dialog" role="document" style={{ zIndex: 2000, position: 'fixed', top: 40, left: '50%', transform: 'translateX(-50%)', margin: '0 auto', display: 'block', background: 'none', boxShadow: 'none' }}>
          <div className="modal-content delete-confirm-modal" style={{ minWidth: 340 }}>
            <div className="modal-header">
              <h5 className="modal-title">Delete Playlist</h5>
              <button type="button" className="close" aria-label="Close" onClick={() => setShowDeleteModal(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete <b>{playlistToDelete.name}</b>?
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="ml-auto btn-warning-custom btn btn-secondary" onClick={async () => { await handleDeletePlaylist(playlistToDelete); }}>
                <span>Continue</span>
              </button>
              <button type="button" className="ml-2 btn-primary-custom btn btn-secondary" onClick={() => { setShowDeleteModal(false); setPlaylistToDelete(null); }}>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Custom Clear Playlist Modal */}
      {showClearModal && playlistToClear && (
        <div className="modal-dialog" role="document" style={{ zIndex: 2000, position: 'fixed', top: 40, left: '50%', transform: 'translateX(-50%)', margin: '0 auto', display: 'block', background: 'none', boxShadow: 'none' }}>
          <div className="modal-content delete-confirm-modal" style={{ minWidth: 340 }}>
            <div className="modal-header">
              <h5 className="modal-title">Clear Playlist</h5>
              <button type="button" className="close" aria-label="Close" onClick={() => setShowClearModal(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to remove all tracks from <b>{playlistToClear.name}</b>?
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="ml-auto btn-warning-custom btn btn-secondary" onClick={async () => { await handleClearPlaylist(playlistToClear); }}>
                <span>Continue</span>
              </button>
              <button type="button" className="ml-2 btn-primary-custom btn btn-secondary" onClick={() => { setShowClearModal(false); setPlaylistToClear(null); }}>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Custom Download Playlist Modal */}
      {showDownloadModal && downloadModalInfo && (
        <div className="modal-dialog" role="document" style={{ zIndex: 2000, position: 'fixed', top: 40, left: '50%', transform: 'translateX(-50%)', margin: '0 auto', display: 'block', background: 'none', boxShadow: 'none' }}>
          <div className="modal-content delete-confirm-modal" style={{ minWidth: 340 }}>
            <div className="modal-header">
              <h5 className="modal-title">Download Playlist</h5>
              <button type="button" className="close" aria-label="Close" onClick={() => setShowDownloadModal(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {`Download ${downloadModalInfo.trackCount} track(s) from `}
              <b>{downloadModalInfo.playlist.name}</b>
              {downloadModalInfo.fileType === 'mp3' ? ' as .mp3 zip?' : ' as .wave zip?'}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="ml-auto btn-warning-custom btn btn-secondary" onClick={async () => {
                await handleDownloadAllTracks(downloadModalInfo.playlist.id, downloadModalInfo.fileType);
                setShowDownloadModal(false);
                setDownloadModalInfo(null);
              }}>
                <span>Continue</span>
              </button>
              <button type="button" className="ml-2 btn-primary-custom btn btn-secondary" onClick={() => { setShowDownloadModal(false); setDownloadModalInfo(null); }}>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Progress Overlay */}
      {downloadProgress.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, boxShadow: '0 2px 12px #0008', minWidth: 280, textAlign: 'center' }}>
            <h5 style={{ marginBottom: 16 }}>Preparing Download...</h5>
            <div style={{ fontSize: 18, marginBottom: 8 }}>
              Downloaded {downloadProgress.current} of {downloadProgress.total} files
            </div>
            <div style={{ width: '100%', background: '#eee', borderRadius: 8, height: 12, marginBottom: 8 }}>
              <div style={{ width: `${downloadProgress.total ? (100 * downloadProgress.current / downloadProgress.total) : 0}%`, background: 'var(--accent-1-color)', height: '100%', borderRadius: 8, transition: 'width 0.2s' }}></div>
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              This may take a while for large playlists.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistsRow; 